/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import type { InjectedRouter } from 'react-router';

import { useQuery } from '@apollo/client';
import {
  ProgramAdministratorPrivilegesConfirmation,
  useInviteAcceptance,
  withInviteAcceptance,
  withResellerDPA,
} from '@coursera-web/enterprise-admin-onboarding';
import type { PropsFromUseInviteAcceptance } from '@coursera-web/enterprise-admin-onboarding';
import PropTypes from 'prop-types';
import type Q from 'q';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import createLoadableComponent from 'js/lib/createLoadableComponent';
import getModernContext from 'js/lib/getModernContext';
import { useParams } from 'js/lib/useRouter';
import { get as getLoggedInUser, isAuthenticatedUser, isPoweruser } from 'js/lib/user';

import { LoadingSection } from '@coursera/cds-core';

import AdminDashboardPageGraphqlDGS from 'bundles/admin-dashboard/queries/AdminDashboardPage.graphql';
import type {
  AdminDashboardPageQuery as AdminDashboardPageQueryDGS,
  AdminDashboardPageQueryVariables as AdminDashboardPageQueryVariablesDGS,
} from 'bundles/admin-dashboard/queries/__generated__/AdminDashboardPage';
import { initializeDrift } from 'bundles/admin-dashboard/utils/drift';
import { initializePendo } from 'bundles/admin-dashboard/utils/pendo';
import { SKILL_TRACK_CONTRACT_TAGS } from 'bundles/enterprise-admin-contracts/constants';
import { allContractsHardPausedOrTerminated } from 'bundles/enterprise-admin-contracts/enterprise-admin-contracts-index';
import withCourseraForCampusBasicSetup from 'bundles/enterprise-admin-coursera-for-campus-basic/components/withCourseraForCampusBasicSetup';
import PrivateAuthoringPartnerProvider from 'bundles/enterprise-admin-custom-courses/components/PrivateAuthoringPartnerProvider';
import { useEnableLearnerGroups } from 'bundles/enterprise-admin-hooks/hooks/useEnableLearnerGroups';
import { useEnableMessagingV2 } from 'bundles/enterprise-admin-hooks/hooks/useFeatures';
import { RECRUITER_TEMPLATE_ID } from 'bundles/enterprise-admin-management/constants';
import { mapTemplateIdToUntranslatedTemplateName } from 'bundles/enterprise-admin-management/util/TemplateMapper';
import { LearningPathCreationProvider } from 'bundles/enterprise-admin-program-creation/components/context/learningPathCreationContext';
import type { EnterpriseAdminPermissionsContextProps } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import {
  EnterpriseAdminPermissionsContext,
  useEnterpriseAdminPermissions,
} from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import withEnterpriseAdminRolePermissionsRequired from 'bundles/enterprise-admin-roles/components/withEnterpriseAdminRolePermissionsRequired';
import { adminDashboardPageBaseQuery, adminDashboardPageQuery } from 'bundles/enterprise-admin/AdminDashboardPageQuery';
import type { Contract } from 'bundles/enterprise-admin/EnterpriseAdminContext';
import { EnterpriseAdminContextProvider } from 'bundles/enterprise-admin/EnterpriseAdminContext';
import ThirdPartyOrganizationProvider from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';
import type {
  AdminDashboardPageBaseQuery,
  AdminDashboardPageBaseQueryVariables,
} from 'bundles/enterprise-admin/__generated__/AdminDashboardPageBaseQuery';
import type {
  AdminDashboardPageQueryVariables,
  AdminDashboardPageQuery as AdminDashboardQueryData,
} from 'bundles/enterprise-admin/__generated__/AdminDashboardPageQuery';
import { org_coursera_product_enterprise_contract_EnterpriseContractState as EnterpriseContractState } from 'bundles/enterprise-admin/__generated__/globalTypes';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import { withProductFeatures } from 'bundles/enterprise-admin/features';
import type { ThirdPartyOrganization } from 'bundles/enterprise-admin/types';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import { Header, NoActiveContractsFound, NotAuthorized, OrgNotFound } from 'bundles/enterprise-ui/components';
import type { ModalContextType } from 'bundles/enterprise-ui/components/ModalContext';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import { IdTypes } from 'bundles/product-features';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import AuthenticationOption from 'bundles/user-account/components/AuthenticationOption';

import _t from 'i18n!nls/admin-dashboard';

type PropsFromWithProductFeatures = {
  enableAcademicDisciplines: boolean;
  enableSkillsPlanningForOrg: boolean;
  enableEnhancedLearnerProfilesForAdmin: boolean;
  enableNewProgramCreation: boolean;
  isGwGOrg: boolean;
  isGwGFreemiumOrg: boolean;
  isGwGPaidOrg: boolean;
  isC4CBOrg: boolean;
  isFreemiumOrg: boolean;
  allowOrgForCurriculumBuilder: boolean;
  allowOrgForMoocFixedTerms: boolean;
  isCareerAcademySelfServeEnabled: boolean;
  enableCareerAcademyLimitedRestrictions: boolean;
  enableGradebookPlagiarismReport: boolean;
  enableDataTechSku: boolean;
  enableAssignContent: boolean;
};

const DeferredSurveyNpsModal = createLoadableComponent(
  () => import('bundles/admin-dashboard/components/nps/SurveyNpsModal')
);

type PropsFromCaller = {
  withGlobalNavigation?: boolean;
  withStyleGuide?: boolean;
};

type PropsFromRouter = { thirdPartySlug: string };

type PropsFromBaseGraphql = {
  thirdPartyOrganization?: ThirdPartyOrganization;
  onOrganizationUpdated?: () => Promise<$TSFixMe>;
};

type PropsFromBranch = {
  thirdPartyOrganization: ThirdPartyOrganization;
};

type PropsFromGraphql = {
  contracts: Contract[];
  contractTags: string[];
  hasSkillSets: boolean;
  roles: string[];
  adminRoleType?: string;
  onAdminRolesUpdated?: () => Promise<$TSFixMe>;
};

type PropsFromEnterprisePermissionsContext = {
  enterpriseAdminPermissionsContext: EnterpriseAdminPermissionsContextProps;
};

type PropsFromEnableLearnersPage = {
  enableNewLearnersPage: boolean;
};

type PropsFromEnableLearnerGroups = {
  enableLearnerGroups: boolean;
};

type PropsFromEnableMessagingV2 = {
  enableMessagingV2: boolean;
};

export type AdminDashboardPageProps = PropsFromCaller & {
  children: JSX.Element;
} & PropsFromBaseGraphql &
  PropsFromBranch &
  PropsFromGraphql &
  PropsFromWithProductFeatures &
  PropsFromEnterprisePermissionsContext &
  PropsFromEnableLearnersPage &
  PropsFromEnableLearnerGroups &
  PropsFromEnableMessagingV2;

type Props = AdminDashboardPageProps;

type Context = {
  router: InjectedRouter;
  addToast: (message: string) => void;
  executeMutation: <Mutation, Response>(mutation: Mutation) => Q.Promise<Response>;
} & ModalContextType;

type State = { adminRoleGrantedJustNow: boolean };

export class AdminDashboardPage extends React.Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    openModal: PropTypes.func,
    closeModal: PropTypes.func,
    executeMutation: PropTypes.func,
    addToast: PropTypes.func,
  };

  declare context: LegacyContextType<typeof AdminDashboardPage.contextTypes>;

  static childContextTypes = {
    enableSkillsPlanningForOrg: PropTypes.bool,
    enableEnhancedLearnerProfilesForAdmin: PropTypes.bool,
    enableNewProgramCreation: PropTypes.bool,
    onOrganizationUpdated: PropTypes.func,
    allowOrgForCurriculumBuilder: PropTypes.bool,
    allowOrgForMoocFixedTerms: PropTypes.bool,
    isCareerAcademySelfServeEnabled: PropTypes.bool,
    enableAcademicDisciplines: PropTypes.bool,
    enableGradebookPlagiarismReport: PropTypes.bool,
    enableDataTechSku: PropTypes.bool,
    enableNewLearnersPage: PropTypes.bool,
    enableLearnerGroups: PropTypes.bool,
    enableMessagingV2: PropTypes.bool,
  };

  state: State = {
    adminRoleGrantedJustNow: false,
  };

  getChildContext() {
    const {
      enableAcademicDisciplines,
      enableEnhancedLearnerProfilesForAdmin,
      enableNewProgramCreation,
      onOrganizationUpdated,
      allowOrgForMoocFixedTerms,
      allowOrgForCurriculumBuilder,
      isCareerAcademySelfServeEnabled,
      enableGradebookPlagiarismReport,
      enableNewLearnersPage,
      enableLearnerGroups,
      enableMessagingV2,
    } = this.props;
    return {
      enableAcademicDisciplines,
      enableSkillsPlanningForOrg: this.getIsSkillSetsEnabled(),
      enableEnhancedLearnerProfilesForAdmin: isPoweruser() || enableEnhancedLearnerProfilesForAdmin,
      enableNewProgramCreation:
        enableNewProgramCreation || isCareerAcademySelfServeEnabled || enableAcademicDisciplines,
      onOrganizationUpdated,
      allowOrgForCurriculumBuilder,
      allowOrgForMoocFixedTerms,
      isCareerAcademySelfServeEnabled,
      enableGradebookPlagiarismReport,
      enableNewLearnersPage,
      enableLearnerGroups,
      enableMessagingV2,
    };
  }

  constructor(props: Props, context: Context) {
    super(props, context);
    const {
      contracts = [],
      thirdPartyOrganization,
      isGwGOrg,
      isGwGFreemiumOrg,
      isGwGPaidOrg,
      isC4CBOrg,
      isFreemiumOrg,
    } = props;
    const firstContract = contracts.find((_) => _.contractState === EnterpriseContractState.ACTIVE);
    enterpriseAdminUser.setContractType(firstContract && firstContract.contractType);
    // freemium orgs
    enterpriseAdminUser.setIsFreemiumOrg(isFreemiumOrg);
    enterpriseAdminUser.setIsC4CBOrg(isC4CBOrg);
    // gwg orgs
    enterpriseAdminUser.setIsGwGOrg(isGwGOrg);
    enterpriseAdminUser.setIsGwGFreemiumOrg(isGwGFreemiumOrg);
    enterpriseAdminUser.setIsGwGPaidOrg(isGwGPaidOrg);
    // c4c
    enterpriseAdminUser.setIsC4COrg(thirdPartyOrganization.organizationType === 'CAMPUS');
  }

  getIsSkillSetsEnabled() {
    const { hasSkillSets, enableSkillsPlanningForOrg, enableCareerAcademyLimitedRestrictions, contracts } = this.props;
    const isGwGOrg = enterpriseAdminUser.isGwGOrg;

    return (
      !enableCareerAcademyLimitedRestrictions &&
      !isGwGOrg &&
      (enableSkillsPlanningForOrg || hasSkillSets) &&
      !contracts
        .filter((_) => _.contractState === EnterpriseContractState.ACTIVE)
        .every((contract) => SKILL_TRACK_CONTRACT_TAGS.includes(contract.contractTag?.contractTagName ?? ''))
    );
  }

  handleAdminPrivilegesConfirmed = () => {
    const { router } = this.context;
    this.setState({ adminRoleGrantedJustNow: false });
    this.initializeThirdPartyServices();
    const newQuery = { ...router.location.query };
    delete newQuery.showProgramAdminPrivilegesGrantedModal;
    router.push({
      ...router.location,
      params: router.params,
      query: newQuery,
    });
  };

  showAdminPrivilegesGrantedModal = () => {
    this.setState({ adminRoleGrantedJustNow: true });
  };

  initializeThirdPartyServices() {
    const {
      contracts = [],
      contractTags = [],
      thirdPartyOrganization,
      isCareerAcademySelfServeEnabled,
      roles,
    } = this.props;
    initializeDrift(thirdPartyOrganization.slug);
    initializePendo({
      contracts,
      contractTags,
      thirdPartyOrganization,
      isSkillSetsEnabled: this.getIsSkillSetsEnabled(),
      isCareerAcademySelfServeEnabled,
      roles,
    });
  }

  isAuthorized = () => {
    const { enterpriseAdminPermissionsContext } = this.props;
    const { canViewOrg } = enterpriseAdminPermissionsContext;
    return enterpriseAdminUser.isSuperUserOrEnterpriseOutsourcing() || canViewOrg;
  };

  componentDidMount() {
    const { router } = this.context;

    if (isAuthenticatedUser() && router.location.query.showProgramAdminPrivilegesGrantedModal) {
      this.showAdminPrivilegesGrantedModal();
    } else if (this.isAuthorized()) {
      this.initializeThirdPartyServices();
    }
  }

  render() {
    const { router } = this.context;
    const {
      children,
      thirdPartyOrganization,
      contracts,
      hasSkillSets,
      enableDataTechSku,
      enableAssignContent,
      adminRoleType,
    } = this.props;
    const { adminRoleGrantedJustNow } = this.state;
    const authorized = this.isAuthorized();
    const authenticated = isAuthenticatedUser();
    if (authenticated && !authorized) {
      return (
        <React.Fragment>
          <Header />
          <NotAuthorized />
        </React.Fragment>
      );
    }

    if (allContractsHardPausedOrTerminated(contracts) && !isPoweruser()) {
      return <NoActiveContractsFound />;
    }

    return (
      <EnterpriseAdminContextProvider
        enableDataTechSku={enableDataTechSku}
        enableAssignContent={enableAssignContent}
        hasBillableContracts={contracts.some((contract) => contract.billingType !== 'NONE')}
        hasNonBillableContracts={contracts.some((contract) => contract.billingType === 'NONE')}
        hasSkillSets={hasSkillSets}
        contracts={contracts}
        adminRoleType={adminRoleType}
      >
        <ThirdPartyOrganizationProvider thirdPartySlug={router.params.thirdPartySlug}>
          <PrivateAuthoringPartnerProvider thirdPartyOrganizationId={thirdPartyOrganization.id}>
            <LearningPathCreationProvider>
              <div className="rc-AdminDashboardPage">
                <AuthenticationOption />
                <div className="dashboard-app-container">
                  {authenticated && authorized // otherwise users can see the zero state ui
                    ? children
                    : undefined}
                </div>
                {authenticated && <DeferredSurveyNpsModal thirdPartyOrganization={thirdPartyOrganization} />}
                <CourseraMetatags metaNameAndProperties={{ title: `${thirdPartyOrganization.name} ${_t('Admin')}` }} />
                {adminRoleGrantedJustNow && (
                  <ProgramAdministratorPrivilegesConfirmation onConfirm={this.handleAdminPrivilegesConfirmed} />
                )}
              </div>
            </LearningPathCreationProvider>
          </PrivateAuthoringPartnerProvider>
        </ThirdPartyOrganizationProvider>
      </EnterpriseAdminContextProvider>
    );
  }
}

const EnhancedAdminDashboardPage = compose<
  Props,
  PropsFromCaller &
    PropsFromRouter &
    PropsFromBaseGraphql &
    PropsFromBranch &
    PropsFromGraphql &
    PropsFromUseInviteAcceptance
>(
  withProductFeatures<PropsFromWithProductFeatures, { thirdPartyOrganization: { id: string } }>(
    ({ thirdPartyOrganization }) => ({ idType: IdTypes.Organization, id: thirdPartyOrganization.id }),
    ({ features }) => ({
      enableAcademicDisciplines: features.get('enterprise', 'enableAcademicDisciplines'),
      enableSkillsPlanningForOrg: features.get('enterprise', 'skillsEnableForEnterpriseAdmins'),
      enableEnhancedLearnerProfilesForAdmin: features.get('enterprise', 'enableEnhancedLearnerProfilesForAdmin'),
      enableNewProgramCreation:
        features.get('enterprise', 'enableNewProgramCreation') ||
        (features.get('enterprise', 'allowOrgForCurriculumBuilder') &&
          features.get('enterprise', 'enableAcademicDisciplines')),
      isGwGOrg: features.get('enterprise', 'enableGwG'),
      isGwGFreemiumOrg: features.get('enterprise', 'enableGwGFreemium'),
      isGwGPaidOrg: features.get('enterprise', 'enableGwGPaid'),
      isC4CBOrg: features.get('enterprise', 'enableC4CBFreemium'),
      isFreemiumOrg: features.get('enterprise', 'enableFreemium'),
      allowOrgForCurriculumBuilder: features.get('enterprise', 'allowOrgForCurriculumBuilder'),
      allowOrgForMoocFixedTerms: features.get('enterprise', 'allowOrgForMoocFixedTerms'),
      isCareerAcademySelfServeEnabled: features.get('enterprise', 'isCareerAcademySelfServeEnabled'),
      enableCareerAcademyLimitedRestrictions: features.get('enterprise', 'enableCareerAcademyLimitedRestrictions'),
      enableGradebookPlagiarismReport: features.get('enterprise', 'enableGradebookPlagiarismReport'),
      enableDataTechSku: features.get('enterprise', 'enableDataTechSku'),
      enableAssignContent: features.get('enterprise', 'enableAssignContent'),
    })
  ),
  withInviteAcceptance,
  withResellerDPA,
  withCourseraForCampusBasicSetup,
  getModernContext('enterpriseAdminPermissionsContext', EnterpriseAdminPermissionsContext),
  withEnterpriseAdminRolePermissionsRequired
)(AdminDashboardPage);

const AdminDashboardPageWithHooks: React.LegacyFunctionComponentWithChildren<PropsFromCaller> = (props) => {
  const { thirdPartySlug } = useParams();
  const {
    data: orgData,
    loading: loadingOrg,
    refetch: onOrganizationUpdated,
  } = useQuery<AdminDashboardPageBaseQuery, AdminDashboardPageBaseQueryVariables>(adminDashboardPageBaseQuery, {
    // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    variables: { thirdPartySlug },
    skip: !thirdPartySlug,
  });
  const thirdPartyOrganization = orgData?.ThirdPartyOrganizationsV1Resource?.bySlug?.elements?.[0];

  const { data: dgsData, loading: loadingDgs } = useQuery<
    AdminDashboardPageQueryDGS,
    AdminDashboardPageQueryVariablesDGS
  >(AdminDashboardPageGraphqlDGS, {
    variables: {
      organizationId: String(thirdPartyOrganization?.id),
      thirdPartyOrganizationId: String(thirdPartyOrganization?.id),
      userId: String(getLoggedInUser().id),
    },
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    skip: !thirdPartyOrganization?.id || !isAuthenticatedUser(),
  });
  const contracts = dgsData?.EnterpriseContract?.queryContractsByOrgId.elements ?? [];
  const roles =
    dgsData?.EnterpriseUserRoles?.queryGrantedRolesByUserOrganization.map((_) => String(_.universalRole)) ?? [];
  const roleTemplates = dgsData?.EnterpriseUserRoles?.queryGrantedRolesByUserOrganization.map((_) => _.templateId);
  const isRecruiter = roleTemplates?.length === 1 && roleTemplates?.includes(RECRUITER_TEMPLATE_ID);
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const adminRoleType = roleTemplates?.length ? mapTemplateIdToUntranslatedTemplateName(roleTemplates[0]) : undefined;
  const {
    data,
    refetch: refetchAdminDashboardPageQuery,
    loading,
  } = useQuery<AdminDashboardQueryData, AdminDashboardPageQueryVariables>(adminDashboardPageQuery, {
    skip: !thirdPartyOrganization?.id || !isAuthenticatedUser(),
    variables: { orgId: String(thirdPartyOrganization?.id) },
  });

  const contractTags = contracts.reduce((all, contract) => {
    const tag = contract.contractTag?.contractTagName;
    return tag ? [...all, tag] : all;
  }, [] as string[]);
  const hasSkillSets =
    filterExistsOrDefault(data?.EnterpriseTargetSkillProfilesV1Resource?.byOrg?.elements, []).length > 0;

  const { acceptAllInvitationsForEmailAndOrganization, sendVerificationEmail, onInviteAccepted } =
    useInviteAcceptance();
  const { onPermissionsUpdated } = useEnterpriseAdminPermissions();
  const enableLearnerGroups = useEnableLearnerGroups(thirdPartyOrganization?.id);
  const enableMessagingV2 = useEnableMessagingV2(thirdPartyOrganization?.id ?? '');

  React.useEffect(() => {
    if (isRecruiter) {
      location.href = `/recruiting/${thirdPartySlug}/candidates/dashboard`;
    }
  }, [thirdPartySlug, isRecruiter]);

  if (loadingOrg || loadingDgs || loading || isRecruiter) {
    return <LoadingSection css={{ height: '100vh' }} />;
  }

  if (!thirdPartyOrganization) {
    return <OrgNotFound />;
  }

  const enableNewLearnersPage = EnterpriseExperiments.get('enableNewLearnersPage');

  return (
    <EnhancedAdminDashboardPage
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      thirdPartySlug={thirdPartySlug}
      thirdPartyOrganization={thirdPartyOrganization}
      // @ts-expect-error DateTime: string
      contracts={contracts}
      contractTags={contractTags}
      hasSkillSets={hasSkillSets}
      roles={roles}
      adminRoleType={adminRoleType}
      onOrganizationUpdated={onOrganizationUpdated}
      onAdminRolesUpdated={() => {
        return Promise.all([refetchAdminDashboardPageQuery(), onPermissionsUpdated()]);
      }}
      acceptAllInvitationsForEmailAndOrganization={acceptAllInvitationsForEmailAndOrganization}
      sendVerificationEmail={sendVerificationEmail}
      onInviteAccepted={onInviteAccepted}
      enableNewLearnersPage={enableNewLearnersPage}
      enableLearnerGroups={enableLearnerGroups}
      enableMessagingV2={enableMessagingV2}
      {...props}
    />
  );
};

export default AdminDashboardPageWithHooks;
